import { useEffect, useCallback } from 'react';
import { X, ChevronRight, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { getAgentColor, getAgentBySlug } from '../../utils/agents';
import { parseAgentProfile, stripProfileFromMarkdown } from '../../utils/agentProfile';
import { StatusBadge } from '../common/StatusBadge';
import type { AgentDetail as AgentDetailType } from '../../types/agents';
import { formatDistanceToNow } from 'date-fns';

function getInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return name.substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function mapStatus(status: string): 'success' | 'warning' | 'error' | 'info' | 'running' {
  switch (status) {
    case 'success': return 'success';
    case 'failed': case 'error': return 'error';
    case 'running': return 'running';
    case 'skipped': return 'warning';
    default: return 'info';
  }
}

interface AgentDetailProps {
  agent: AgentDetailType;
  onClose: () => void;
  onNavigate: (slug: string) => void;
}

export default function AgentDetail({ agent, onClose, onNavigate }: AgentDetailProps) {
  const navigate = useNavigate();
  const colorClass = getAgentColor(agent.department);
  const initials = getInitials(agent.name);
  const supervisor = agent.reportsTo !== 'owner' ? getAgentBySlug(agent.reportsTo) : null;
  const directReportAgents = agent.directReports.map(slug => getAgentBySlug(slug)).filter(Boolean);
  const visibleActivity = agent.recentActivity.slice(0, 5);
  const profile = agent.markdownContent ? parseAgentProfile(agent.markdownContent) : null;
  const cleanedMarkdown = agent.markdownContent ? stripProfileFromMarkdown(agent.markdownContent) : null;

  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[640px] bg-bg-secondary border-l border-border-subtle z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-bg-secondary border-b border-border-subtle p-5 z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full ${colorClass} flex items-center justify-center flex-shrink-0`}>
                <span className="text-sm font-bold text-white">{initials}</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">{agent.name}</h2>
                <p className="text-sm text-text-secondary">{agent.title}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${colorClass}`}>
                  {agent.isCSuite ? 'C-Suite' : agent.department}
                </span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Reports To */}
          {supervisor ? (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">Reports To</h3>
              <button
                onClick={() => onNavigate(supervisor.slug)}
                className="flex items-center gap-2 text-sm text-accent-purple-light hover:underline"
              >
                <span>{supervisor.name}</span>
                <span className="text-text-muted">({supervisor.shortName})</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </section>
          ) : (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">Reports To</h3>
              <span className="text-sm text-text-secondary">Owner</span>
            </section>
          )}

          {/* Direct Reports */}
          {directReportAgents.length > 0 ? (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">
                Direct Reports ({directReportAgents.length})
              </h3>
              <div className="space-y-1">
                {directReportAgents.map(report => (
                  <button
                    key={report!.slug}
                    onClick={() => onNavigate(report!.slug)}
                    className="flex items-center gap-2 text-sm text-text-secondary hover:text-accent-purple-light w-full text-left py-1"
                  >
                    <div className={`w-6 h-6 rounded-full ${getAgentColor(report!.department)} flex items-center justify-center`}>
                      <span className="text-[9px] font-bold text-white">
                        {getInitials(report!.name)}
                      </span>
                    </div>
                    <span>{report!.name}</span>
                    <span className="text-text-muted text-xs">- {report!.shortName}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {/* Skills */}
          {agent.skills.length > 0 ? (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">
                Skills ({agent.skills.length})
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {agent.skills.map(skill => (
                  <span
                    key={skill}
                    className="px-2 py-1 rounded bg-bg-tertiary text-xs text-text-secondary font-mono"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {/* About (Profile) */}
          {profile ? (
            <section className="bg-bg-tertiary rounded-lg p-4 space-y-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted">About</h3>

              {profile.motto ? (
                <div className="relative pl-4 border-l-2 border-accent-purple">
                  <p className="text-base italic text-text-primary leading-relaxed">
                    &ldquo;{profile.motto}&rdquo;
                  </p>
                </div>
              ) : null}

              {profile.background ? (
                <p className="text-sm text-text-secondary leading-relaxed">{profile.background}</p>
              ) : null}

              {profile.personality ? (
                <div className="flex flex-wrap gap-1.5">
                  {profile.personality.split(',').map(trait => (
                    <span
                      key={trait.trim()}
                      className="px-2 py-0.5 rounded-full bg-accent-purple/15 text-xs text-accent-purple-light font-medium"
                    >
                      {trait.trim()}
                    </span>
                  ))}
                </div>
              ) : null}

              {profile.communicationStyle ? (
                <p className="text-sm italic text-text-muted leading-relaxed">{profile.communicationStyle}</p>
              ) : null}
            </section>
          ) : null}

          {/* Agent Definition (Markdown) */}
          {cleanedMarkdown ? (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">Agent Definition</h3>
              <div className="prose-dark bg-bg-tertiary rounded-lg p-4 overflow-x-auto">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-lg font-bold text-text-primary mt-4 mb-2 first:mt-0">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-semibold text-text-primary mt-4 mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold text-text-primary mt-3 mb-1.5">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-sm font-medium text-text-secondary mt-2 mb-1">{children}</h4>,
                    p: ({ children }) => <p className="text-sm text-text-secondary mb-2 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside text-sm text-text-secondary mb-2 space-y-0.5">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside text-sm text-text-secondary mb-2 space-y-0.5">{children}</ol>,
                    li: ({ children }) => <li className="text-sm text-text-secondary">{children}</li>,
                    a: ({ href, children }) => {
                      if (href && (href.endsWith('.md') || href.includes('.md#'))) {
                        const mdPath = href.startsWith('/') ? href : href;
                        return (
                          <button
                            onClick={() => {
                              const resolved = mdPath.startsWith('/opt/') || mdPath.startsWith('/var/')
                                ? mdPath
                                : '/opt/union-threads/' + mdPath.replace(/^\.\//, '');
                              navigate(`/docs?path=${encodeURIComponent(resolved)}`);
                            }}
                            className="text-accent-purple-light hover:underline cursor-pointer"
                          >
                            {children}
                          </button>
                        );
                      }
                      return <a href={href} className="text-accent-purple-light hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>;
                    },
                    code: ({ className, children }) => {
                      const isInline = !className;
                      if (isInline) {
                        return <code className="bg-bg-secondary px-1.5 py-0.5 rounded text-xs text-accent-purple-light font-mono">{children}</code>;
                      }
                      return <code className="block bg-bg-secondary rounded p-3 text-xs text-text-secondary font-mono overflow-x-auto my-2">{children}</code>;
                    },
                    pre: ({ children }) => <pre className="bg-bg-secondary rounded p-3 overflow-x-auto my-2">{children}</pre>,
                    blockquote: ({ children }) => <blockquote className="border-l-2 border-accent-purple pl-3 my-2 text-text-muted italic">{children}</blockquote>,
                    table: ({ children }) => <table className="w-full text-sm text-text-secondary my-2 border-collapse">{children}</table>,
                    thead: ({ children }) => <thead className="border-b border-border-subtle">{children}</thead>,
                    th: ({ children }) => <th className="text-left py-1.5 px-2 text-xs font-medium text-text-muted uppercase">{children}</th>,
                    td: ({ children }) => <td className="py-1.5 px-2 border-b border-border-subtle text-sm">{children}</td>,
                    hr: () => <hr className="border-border-subtle my-4" />,
                    strong: ({ children }) => <strong className="font-semibold text-text-primary">{children}</strong>,
                    em: ({ children }) => <em className="italic text-text-secondary">{children}</em>,
                  }}
                >
                  {cleanedMarkdown}
                </ReactMarkdown>
              </div>
            </section>
          ) : null}

          {/* Recent Activity */}
          {visibleActivity.length > 0 ? (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2">
                Recent Activity
              </h3>
              <div className="space-y-2">
                {visibleActivity.map(event => (
                  <div
                    key={event.id}
                    className="flex items-start gap-3 p-3 bg-bg-tertiary rounded-lg"
                  >
                    <div className="mt-0.5">
                      <StatusBadge status={mapStatus(event.status)} size="sm" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-text-primary truncate">{event.summary}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-xs text-text-muted">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                        </span>
                        {event.duration_ms != null ? (
                          <span className="text-xs text-text-muted">
                            {(event.duration_ms / 1000).toFixed(1)}s
                          </span>
                        ) : null}
                        {event.cost_usd > 0 ? (
                          <span className="text-xs text-text-muted">
                            ${event.cost_usd.toFixed(4)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}
